import { useEffect, useState, useRef } from "react";
import { apiClient, apiUrl } from "@/api/api";
import { useSelectedPersona } from "@/hooks/use-selected-persona";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Rating } from "@/components/ui/rating";
import { usePersonasQuery } from "@/api/personas";
import { usePersonaLorasQuery } from "@/api/loras";
import { useNavigate } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LoaderCircle,
  Camera,
  Sparkles,
  Wand2,
  BrainCircuit,
  Database,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { MODEL_CONFIGS, getModelLabel } from "@/config/models";
import { Carousel } from "@/components/ImageGen/Carousel";
import LoraTrainingPage from "@/components/ImageGen/LoraTrainingPage";
import {
  useReviewContentQuery,
  useDeleteReviewContentMutation,
  usePerformActionOnReviewContentMutation,
} from "@/api/content-review";
import { useTabUrlState } from "@/hooks/use-tab-url-state";

const Timer = ({
  isRunning,
  finalTime,
}: {
  isRunning: boolean;
  finalTime?: number;
}) => {
  const [time, setTime] = useState(0);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    let animationFrameId: number | undefined;

    if (isRunning) {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }

      const updateTimer = () => {
        const currentTime = Date.now();
        const elapsedTime = (currentTime - (startTimeRef.current || 0)) / 1000;
        setTime(elapsedTime);
        animationFrameId = requestAnimationFrame(updateTimer);
      };

      animationFrameId = requestAnimationFrame(updateTimer);
    } else if (finalTime !== undefined) {
      setTime(finalTime);
      startTimeRef.current = null;
    } else {
      setTime(0);
      startTimeRef.current = null;
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isRunning, finalTime]);

  return (
    <div className="flex items-center justify-center w-full">
      <div className="flex items-center justify-center gap-2">
        <span className="text-white/60 text-sm">Generation time:</span>
        <span className="text-sm font-mono text-primary">
          {time.toFixed(2)}s
        </span>
      </div>
    </div>
  );
};

function ImageGenerationPage() {
  const tabs = ["newmodel", "custommodels"];
  const [activeTab, setActiveTab] = useTabUrlState(tabs, "");
  const [selectedPersonaId, setSelectedPersonaId] = useSelectedPersona();
  const { data: personas = [] } = usePersonasQuery();
  const [userPrompt, setUserPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<any>(null);
  const [promptText, setPromptText] = useState("");
  const [selectedModel, setSelectedModel] = useState("fal-ai/flux/dev");
  const [generationTime, setGenerationTime] = useState(0);
  const navigate = useNavigate();
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const { toast } = useToast();

  // Add refresh trigger for carousel
  const [carouselRefreshTrigger] = useState(0);

  // State to toggle between image generation and lora training views
  const isActiveTab = activeTab === "newmodel" || activeTab === "custommodels";

  // New state for model type and lora
  const [imageSubject, setImageSubject] = useState("persona");
  const [modelType, setModelType] = useState("memetica");
  const [selectedLoraId, setSelectedLoraId] = useState("");

  // Fetch loras for the selected persona - with safer data handling
  const { data: personaLorasData, isLoading: isLoadingLoras } =
    usePersonaLorasQuery(
      modelType === "custom" ? selectedPersonaId : undefined,
    );

  // Safely ensure personaLoras is an array before filtering
  const personaLoras = Array.isArray(personaLorasData) ? personaLorasData : [];

  // Filter completed loras
  const completedLoras = personaLoras.filter(
    (lora) => lora.training_status === "completed",
  );

  // Find the currently selected LoRA model
  const selectedLoraModel = completedLoras.find(
    (lora) => lora.lora_id === selectedLoraId,
  );

  const selectedPersona = personas.find(
    (persona) => persona.persona_id === selectedPersonaId,
  );

  useEffect(() => {
    if (selectedPersona && !selectedPersona.avatar_id) {
      setShowAvatarModal(true);
    } else {
      setShowAvatarModal(false);
    }
  }, [selectedPersona]);

  useEffect(() => {
    if (
      personas.length > 0 &&
      personas.every((p) => p.persona_id !== selectedPersonaId)
    ) {
      setSelectedPersonaId(personas[0].persona_id, { replace: true });
    }
  }, [personas, selectedPersonaId, setSelectedPersonaId]);

  // Reset selected lora when model type changes
  useEffect(() => {
    if (modelType !== "custom") {
      setSelectedLoraId("");
    }
  }, [modelType]);

  const { data: previousContent, refetch: refetchPreviousContent } =
    useReviewContentQuery({
      persona: selectedPersonaId,
      status: "image_generated",
      order_by: "timestamp",
    });
  const { mutate: deleteReviewContent } = useDeleteReviewContentMutation();
  const { mutate: performActionOnReviewContent } =
    usePerformActionOnReviewContentMutation();

  useEffect(() => {
    setUserPrompt("");
    setLoading(false);
    setGeneratedContent(null);
    setPromptText("");
    setGenerationTime(0);
  }, [selectedPersonaId]);

  // Return to main view handler
  const handleReturnFromTraining = () => {
    setActiveTab("");
  };

  const handleDelete = async (docId: string) => {
    try {
      await new Promise<void>((resolve, reject) => {
        deleteReviewContent(docId, {
          onSuccess: () => {
            toast({ description: "Content deleted successfully!" });
            refetchPreviousContent(); // Refresh the carousel after deletion
            resolve();
          },
          onError: (error) => {
            console.error("Error deleting content:", error);
            reject(error);
          },
        });
      });
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const handleApprove = async (docId: string) => {
    try {
      await new Promise<void>((resolve, reject) => {
        performActionOnReviewContent(
          { docId, data: { status: "approved" } },
          {
            onSuccess: () => {
              toast({ description: "Content approved successfully!" });
              refetchPreviousContent(); // Refresh the carousel after approval
              resolve();
            },
            onError: (error) => {
              console.error("Error approving content:", error);
              reject(error);
            },
          },
        );
      });
    } catch (error) {
      console.error("Approval failed:", error);
    }
  };

  const handleRate = async (docId: string, rating: number) => {
    try {
      setGeneratedContent((prev: any) => ({
        ...prev,
        rating,
      }));

      await new Promise<void>((resolve, reject) => {
        performActionOnReviewContent(
          { docId, data: { rating } },
          {
            onSuccess: () => {
              toast({ description: "Rating saved successfully!" });
              refetchPreviousContent(); // Refresh the carousel after rating
              resolve();
            },
            onError: (error) => {
              setGeneratedContent((prev: any) => ({
                ...prev,
                rating: prev.rating,
              }));
              console.error("Error saving rating:", error);
              reject(error);
            },
          },
        );
      });
    } catch (error) {
      console.error("Rating failed:", error);
    }
  };

  const fetchPrompt = async () => {
    if (!selectedPersonaId) {
      toast({ description: "No persona selected!" });
      return "";
    }

    try {
      // Use the selected LoRA model name when appropriate
      const productName =
        modelType === "custom" && selectedLoraModel
          ? selectedLoraModel.name
          : MODEL_CONFIGS[selectedModel].label;

      const response = await apiClient.post("/api/generate-image-prompt", {
        persona_id: selectedPersonaId,
        prompt: userPrompt,
        workflow: MODEL_CONFIGS[selectedModel].workflow,
        model: selectedModel,
        product_name: productName,
        image_subject: imageSubject,
        lora_id: modelType === "custom" ? selectedLoraId : undefined,
      });

      if (!response.data || !response.data.prompt) {
        toast({ description: "No prompt received from server" });
        setLoading(false);
        return null;
      }

      const prompt = response.data.prompt;
      setPromptText(prompt);
      return prompt;
    } catch (err: any) {
      console.error("Error fetching prompt:", err);
      if (err.response) {
        toast({
          description: `Error: ${err.response.data.message || "Failed to generate prompt"}`,
        });
      } else {
        toast({ description: "Failed to generate prompt" });
      }
      setLoading(false);
      return null;
    }
  };

  const handleGenerateImage = async () => {
    const prompt = await fetchPrompt();
    if (!prompt) return;

    try {
      // Use the selected LoRA model name when appropriate
      const productName =
        modelType === "custom" && selectedLoraModel
          ? selectedLoraModel.name
          : MODEL_CONFIGS[selectedModel].label;

      const response = await apiClient.post(
        "/api/generate-image",
        {
          persona_id: selectedPersonaId,
          prompt: prompt,
          workflow: MODEL_CONFIGS[selectedModel].workflow,
          model: selectedModel,
          product_name: productName,
          image_subject: imageSubject,
          lora_id: modelType === "custom" ? selectedLoraId : undefined,
        },
        { timeout: 300000 },
      );

      const result = response.data.data;
      if (!result) {
        toast({ description: "No data received from the server." });
        return;
      }

      const imageUrl = result.optimized_url || result.thumbnail_url || "";

      setGeneratedContent({
        docId: result.record_id as string,
        imageSrc: `${apiUrl}/get-file?url=${encodeURIComponent(imageUrl)}`,
        shareText: result.image_caption || "",
        aiRating: parseInt(result.ai_rating, 10) / 20,
        rating: result.rating / 2,
        status: result.status,
        promptText: prompt,
      });

      // Refresh the carousel to show the newly generated image
      refetchPreviousContent();
    } catch (error: any) {
      console.error("Error generating images:", error);
      setLoading(false);

      const status = error.response?.status;

      if (
        status === 400 &&
        error.response?.data?.detail?.includes("Insufficient credits")
      ) {
        toast({
          title: "Insufficient Credits",
          description: `Your agent doesn't have enough credits to generate images. Please add more credits to your account.`,
          variant: "destructive",
          action: (
            <Button
              size="sm"
              variant="outline"
              className="bg-background"
              onClick={() => navigate("/account#credits")}
            >
              Add Credits
            </Button>
          ),
        });
      } else {
        toast({
          title: "Error Generating Images",
          description:
            error.response?.data?.detail ||
            error.message ||
            "An unexpected error occurred",
          variant: "destructive",
        });
      }
    }
  };

  const handleGenerate = async () => {
    setGeneratedContent(null);
    setPromptText("");
    setGenerationTime(0);
    setLoading(true);

    const startTime = Date.now();

    try {
      await handleGenerateImage();
      const endTime = Date.now();
      setGenerationTime((endTime - startTime) / 1000);
    } catch (error) {
      console.error(error);
      toast({ description: "An error occurred during generation." });
    } finally {
      setLoading(false);
    }
  };

  // Main render method with conditional view toggling
  return (
    <>
      <Dialog open={showAvatarModal} onOpenChange={setShowAvatarModal}>
        <DialogContent className="bg-black/90 border-white/10 backdrop-blur-sm">
          <DialogHeader>
            <DialogTitle className="text-primary text-xl">
              Upload Avatar Required
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Upload an Avatar to generate image content for your Agent
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button
              className="bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-medium"
              onClick={() => {
                if (selectedPersonaId) {
                  navigate(`/agents/${selectedPersonaId}/edit#profile`);
                }
              }}
            >
              Go to Profile Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CONDITIONAL RENDERING BASED ON VIEW STATE */}
      {isActiveTab ? (
        // Show Lora Training View
        <LoraTrainingPage onReturn={handleReturnFromTraining} />
      ) : (
        // Show Image Generation View
        <div className="container px-8 py-6 space-y-6">
          <div className="grid sm:grid-flow-col md:grid-cols-12 gap-6">
            {/* Left Box - Controls */}
            <div className="col-span-4">
              <Card className="bg-black/20 border-white/10 backdrop-blur-sm hover:border-primary/20 transition-all duration-200 h-auto">
                <CardHeader className="px-6 pt-6">
                  <CardTitle className="text-2xl font-semibold text-primary">
                    Image Generation
                  </CardTitle>
                  <CardDescription className="text-white/60">
                    Create AI-powered images for your agent
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-6 space-y-6">
                  {/* Image Subject Selector */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-white">
                      Image Subject
                    </Label>
                    <Select
                      value={imageSubject}
                      onValueChange={setImageSubject}
                    >
                      <SelectTrigger className="bg-black/40 border-white/10 hover:border-primary/50 text-white transition-all py-2 px-3">
                        <SelectValue placeholder="Select image subject">
                          {imageSubject === "persona"
                            ? selectedPersona?.name || "Persona"
                            : "Custom Subject"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-black/90 border-white/10 backdrop-blur-md">
                        <SelectItem
                          value="persona"
                          className="text-white hover:bg-[#DFFE00]/10 transition-colors"
                        >
                          {selectedPersona?.name || "Persona"}
                        </SelectItem>
                        <SelectItem
                          value="custom"
                          className="text-white hover:bg-[#DFFE00]/10 transition-colors"
                        >
                          Custom Subject
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* New Model Type Selector */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-white">
                      Model Type
                    </Label>
                    <Select value={modelType} onValueChange={setModelType}>
                      <SelectTrigger className="bg-black/40 border-white/10 hover:border-primary/50 text-white transition-all py-2 px-3">
                        <SelectValue placeholder="Select model type">
                          {modelType === "memetica"
                            ? "Memetica Models"
                            : "Custom Models"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-black/90 border-white/10 backdrop-blur-md">
                        <SelectItem
                          value="memetica"
                          className="text-white hover:bg-[#DFFE00]/10 transition-colors"
                        >
                          Memetica Models
                        </SelectItem>
                        <SelectItem
                          value="custom"
                          className="text-white hover:bg-[#DFFE00]/10 transition-colors"
                        >
                          Custom Models
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Conditional Model Selection */}
                  {modelType === "memetica" ? (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-white">
                        Model Selection
                      </Label>
                      <Select
                        value={selectedModel}
                        onValueChange={setSelectedModel}
                      >
                        <SelectTrigger className="bg-black/40 border-white/10 hover:border-primary/50 text-white transition-all py-2 px-3">
                          <SelectValue placeholder="Select a model">
                            {getModelLabel(selectedModel)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-black/90 border-white/10 backdrop-blur-md">
                          {Object.entries(MODEL_CONFIGS).map(([id, config]) => (
                            <SelectItem
                              key={id}
                              value={id}
                              className="text-white hover:bg-[#DFFE00]/10 transition-colors"
                            >
                              {config.label} - {config.category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-white">
                          Custom Model
                        </Label>
                        <Select
                          value={selectedLoraId}
                          onValueChange={setSelectedLoraId}
                          disabled={
                            isLoadingLoras || completedLoras.length === 0
                          }
                        >
                          <SelectTrigger className="bg-black/40 border-white/10 hover:border-primary/50 text-white transition-all py-2 px-3">
                            <SelectValue
                              placeholder={
                                isLoadingLoras
                                  ? "Loading models..."
                                  : completedLoras.length === 0
                                    ? "No custom models available"
                                    : "Select a custom model"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent className="bg-black/90 border-white/10 backdrop-blur-md">
                            {completedLoras.map((lora) => (
                              <SelectItem
                                key={lora.lora_id}
                                value={lora.lora_id}
                                className="text-white hover:bg-[#DFFE00]/10 transition-colors"
                              >
                                {lora.name} (v{lora.version})
                                {lora.trigger_word && (
                                  <span className="text-white/60 ml-2">
                                    Trigger: {lora.trigger_word}
                                  </span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          className="w-full bg-[#DFFE00]/10 hover:bg-[#DFFE00]/20 text-primary font-medium transition-all duration-200"
                          onClick={() => {
                            setActiveTab("newmodel");
                          }}
                        >
                          <BrainCircuit className="mr-2 h-5 w-5" />
                          Train New Model
                        </Button>
                        <Button
                          className="w-full bg-[#DFFE00]/10 hover:bg-[#DFFE00]/20 text-primary font-medium transition-all duration-200"
                          onClick={() => {
                            setActiveTab("custommodels");
                          }}
                        >
                          <Database className="mr-2 h-5 w-5" />
                          Custom Models
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex-1 space-y-2">
                    <Label className="text-sm font-medium text-white">
                      Create Prompt
                    </Label>
                    <Textarea
                      className="p-4 sm:min-h-[250px] resize-none bg-black/40 border-white/10 hover:border-primary/50 focus:border-primary text-white placeholder-white/40 transition-all"
                      placeholder="Describe your vision or let AI surprise you..."
                      value={userPrompt}
                      onChange={(e) => setUserPrompt(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col 2xl:flex-row gap-3">
                    <Button
                      className="flex-1 h-12 bg-[#DFFE00] hover:bg-[#DFFE00]/90 text-black font-medium transition-all duration-200"
                      onClick={() => handleGenerate()}
                      disabled={
                        !userPrompt.trim() ||
                        loading ||
                        (modelType === "custom" && !selectedLoraId)
                      }
                    >
                      <Camera className="mr-2 h-5 w-5" />
                      Generate
                    </Button>
                    <Button
                      className="flex-1 h-12 bg-[#DFFE00]/10 hover:bg-[#DFFE00]/20 text-primary font-medium transition-all duration-200"
                      variant="ghost"
                      onClick={() => handleGenerate()}
                      disabled={
                        !!userPrompt.trim() ||
                        loading ||
                        imageSubject === "custom" ||
                        (modelType === "custom" && !selectedLoraId)
                      }
                    >
                      <Sparkles className="mr-2 h-5 w-5" />
                      Surprise Me
                    </Button>
                  </div>

                  {promptText && (
                    <div className="pt-6 border-t border-white/10">
                      <Timer
                        isRunning={loading}
                        finalTime={!loading ? generationTime : undefined}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Box - Image Preview */}
            <div className="col-span-8">
              <Card className="bg-black/20 border-white/10 backdrop-blur-sm hover:border-primary/20 transition-all duration-200 h-full">
                <CardContent className="p-6">
                  <div className="w-full h-[650px] rounded-lg border border-white/10 relative overflow-hidden bg-black/40">
                    {loading ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
                        <LoaderCircle className="h-20 w-20 animate-spin text-primary mb-4" />
                        <p className="text-sm text-white/60">
                          Generating your masterpiece...
                        </p>
                      </div>
                    ) : generatedContent?.imageSrc ? (
                      <img
                        src={generatedContent.imageSrc}
                        alt="Generated Content"
                        className="w-full h-full object-contain transition-opacity duration-200"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                        <Wand2 className="h-12 w-12 text-white opacity-20" />
                        <p className="text-white/40">
                          Your creation will appear here
                        </p>
                      </div>
                    )}
                  </div>

                  {generatedContent && (
                    <div className="flex flex-col space-y-4 xl:space-y-0 xl:flex-row xl:justify-between xl:items-center xl:gap-3 mt-4 pt-4 border-t border-white/10">
                      <div className="flex items-center justify-center">
                        <Rating
                          value={generatedContent.rating}
                          max={10}
                          size="md"
                          precision={1}
                          onChange={(newValue) =>
                            handleRate(generatedContent.docId, newValue)
                          }
                        />
                      </div>

                      <div className="flex justify-center gap-3">
                        <Button
                          variant="outline"
                          className="min-w-[100px] border-white/10 hover:border-primary/50 hover:bg-[#DFFE00]/10 text-primary transition-all duration-200"
                          disabled={generatedContent.status === "approved"}
                          onClick={() => handleApprove(generatedContent.docId)}
                        >
                          {generatedContent.status === "approved"
                            ? "Approved"
                            : "Approve"}
                        </Button>
                        <Button
                          variant="outline"
                          className="min-w-[100px] border-white/10 hover:border-red-500/50 hover:bg-red-500/10 text-red-500 transition-all duration-200"
                          onClick={() => handleDelete(generatedContent.docId)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Generated Prompt Card */}
          {promptText && (
            <Card className="bg-black/20 border-white/10 backdrop-blur-sm hover:border-primary/20 transition-all duration-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-primary mb-3">
                  Generated Prompt
                </h3>
                <p className="text-sm text-white/80 p-4 rounded-lg bg-black/40 whitespace-pre-wrap backdrop-blur-sm">
                  {promptText}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Bottom Carousel */}
          {(previousContent?.pages?.[0]?.data?.length ?? 0) > 0 && (
            <div className="pt-2 max-w-auto">
              <Carousel
                items={(previousContent?.pages[0].data ?? []) as any}
                onSelect={(item) => {
                  setGeneratedContent({
                    docId: item.doc_id,
                    imageSrc: `${apiUrl}/get-file?url=${encodeURIComponent(
                      item.optimized_url || item.thumbnail_url,
                    )}`,
                    shareText: item.image_caption,
                    rating: (item.rating ?? 0) / 2,
                    aiRating: parseInt(item.ai_rating ?? "0", 10) / 20,
                    status: item.status,
                  });
                  // @ts-expect-error prompt does not seem to be part of type "item"
                  setPromptText(item.prompt);
                  setGenerationTime(0);
                }}
                displayMode="inline"
                // Use key with refresh trigger to force re-render when data changes
                key={`carousel-${carouselRefreshTrigger}`}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default ImageGenerationPage;
